import { Injectable, Injector, computed, effect, inject, signal, untracked, type Provider, type Signal } from '@angular/core';
import { catchError, firstValueFrom, forkJoin, of } from 'rxjs';
import { EDITOR_VIEWER, type EditorViewer } from '@lite-workspace/shared/design-editor/ports';
import { readAuthUserId } from './auth-identity';

// eslint-disable-next-line @nx/enforce-module-boundaries -- host-port tokens for the lazy album editor route
import {
  ALBUM_EDITOR_ALBUM_EVENTS,
  ALBUM_EDITOR_AUTH,
  ALBUM_EDITOR_BELOW_MIN_CUE,
  ALBUM_EDITOR_COMMENTS,
  ALBUM_EDITOR_DESIGN_OP,
  ALBUM_EDITOR_DOWNLOAD_GUARD,
  ALBUM_EDITOR_FIXED_PHOTOS,
  ALBUM_EDITOR_OUTGOING_BODY,
  ALBUM_EDITOR_PERSONS_READ_GATE,
  ALBUM_EDITOR_RETOUCH,
  type AlbumEditorRetouchPort,
  type AlbumEditorRolesPort,
  ALBUM_EDITOR_ROLES,
  ALBUM_EDITOR_STUDENT_HANDOFF,
  ALBUM_EDITOR_UPLOAD_PROGRESS,
  ALBUM_EDITOR_UPLOAD_QUEUE,
  hasMeasurableQuoteFrames,
  HttpAlbumEditorCommentsData,
  measureQuoteLimits,
  type AlbumEditorDesignOpPort,
  type AlbumEditorFixedPhotosPort,
  type AlbumEditorMasterStructureReadOptions,
  type AlbumEditorStudentHandoffContext,
  type AlbumEditorStudentHandoffPort,
  ALBUM_EDITOR_STUDENT_ITEM,
  type AlbumEditorStudentItem,
  type AlbumEditorStudentItemPort,
} from '@lite-workspace/album-editor/ports';
import { deriveFlowModeFromStored, projectPrintItems } from '@lite-workspace/dto-fotoclass-book';
import { isFolderKind, itemNameOf } from '@lite-workspace/dto-design-system';
import { ToastService } from '@lite-workspace/shared/helm-ui/toast';
import {
  RETOUCH_COPY,
  RETOUCH_LIGHTBOX_PORT,
  RetouchApi,
  RetouchLightboxActComponent,
  RetouchLightboxPillComponent,
  retouchOriginalSrc,
  type RetouchLightboxPhoto,
  type RetouchLightboxPort,
  type RetouchStatus,
} from '@lite-workspace/shared/retouch/host';
import { Router } from '@angular/router';
import { SALES_DATA } from '../features/upsale/domain/infrastructure/http-sales.data';
import type { FramePhotoMeta, PersistedBookModelV1 } from '@lite-workspace/photobook-model';
import { PrintCatalogFacade } from '../features/projects/domain/application/print-catalog.facade';
import { ItemMoveFlowService, studentMoveFrom } from '../features/projects/domain/application/item-move-flow.service';
import type { PrintProductEntry } from '../features/projects/domain/infrastructure/print-services-api.service';
import { firstNameGenitive } from '../features/sync/domain/sync-adaptations';
import { readAccessToken } from './bearer.interceptor';
import { stripClientPreviewMarker } from './client-preview-files.interceptor';
import { EditorUploadQueuePort } from './editor-upload-queue.port';
import { EditorAlbumEventsPort } from './editor-album-events.port';
import { EditorUploadProgressStripComponent } from '../ui/upload';
import { OWNER_PREVIEW_ROLES, ProjectRoleService } from './project-role';
import { ProjectsFacade } from '../features/projects/domain/application/projects.facade';
import { NameWaveSettleService } from '../features/projects/domain/application/name-wave-settle.service';
import { DemoDownloadGuardService } from '../features/projects/ui/demo-download-guard.service';
import { BookAssemblyApiService } from '../features/projects/domain/infrastructure/book-assembly-api.service';
import { RetouchActivityService } from './retouch-activity.service';
import { FotoclassRetouchQueue, retouchOriginalUrl, revertAndSettle } from './retouch-host.providers';

@Injectable({ providedIn: 'root' })
export class StudentHandoffPort implements AlbumEditorStudentHandoffPort {
  private readonly projects = inject(ProjectsFacade);

  handoffContext(projectId: string | null): AlbumEditorStudentHandoffContext {
    const settings = projectId ? this.projects.baseSettingsByProject()[projectId] : undefined;
    return {
      resolved: !!settings,
      // Проект без сохранённого clientSelection — легаси-авто, а не «режим неизвестен».
      flowMode: settings ? deriveFlowModeFromStored(settings.photoBook?.clientSelection) : null,
      masterAlbumId: settings?.photoBook?.masterAlbumId ?? null,
      masterAlbumIds: settings?.photoBook?.masterAlbumIds,
    };
  }

  ensureContext(projectId: string): void {
    // Один повтор: шелл зовёт это единожды на проект, а без снимка настроек шапка fail-closed остаётся
    // вообще без кнопок — одна сетевая икота держала бы её пустой до перезагрузки страницы.
    void this.projects
      .loadProjectBaseSettings(projectId)
      .catch(() => this.projects.loadProjectBaseSettings(projectId))
      .catch(() => undefined);
  }
}

@Injectable({ providedIn: 'root' })
export class EditorDesignOpPort implements AlbumEditorDesignOpPort {
  private readonly assemblyApi = inject(BookAssemblyApiService);
  private readonly projects = inject(ProjectsFacade);
  private readonly injector = inject(Injector);

  /**
   * Тот же серверный пересчёт, что после применения дизайна, плюс перечитка базовых настроек проекта:
   * экран лиц в этой же вкладке читает `facade.quotes(projectId)` и должен увидеть свежий потолок.
   * Одна тихая повторная попытка (сеть моргнула), дальше молчим — следующая сборка пересчитает.
   */
  async designOpCompleted(
    projectId: string,
    book: PersistedBookModelV1,
    ownerPersonId?: string | null,
    leadPersonId?: string | null,
    leadReserve?: number,
  ): Promise<void> {
    const { limitDefault, limitByPerson } = await this.measureQuoteLimit(book, ownerPersonId, leadPersonId, leadReserve);
    try {
      await this.assemblyApi.designOpCompleted(projectId, limitDefault, limitByPerson);
    } catch {
      try {
        await this.assemblyApi.designOpCompleted(projectId, limitDefault, limitByPerson);
      } catch {
        return;
      }
    }

    await this.projects.loadProjectBaseSettings(projectId).catch(() => undefined);
  }

  /** Мерка — общая с оболочкой (`measureQuoteLimits`); здесь только ленивая загрузка холста. */
  private async measureQuoteLimit(
    book: PersistedBookModelV1,
    ownerPersonId?: string | null,
    leadPersonId?: string | null,
    leadReserve?: number,
  ): Promise<{ limitDefault: number | null | undefined; limitByPerson?: Record<string, number> }> {
    // «Рамок нет» — факт дизайна, а не отказ мерки: ответ не зависит от того, загрузился ли холст.
    if (!hasMeasurableQuoteFrames(book, ownerPersonId, leadPersonId)) return { limitDefault: null, limitByPerson: {} };
    try {
      // Мерка тянет за собой весь холст (rust-canvas, шейпер, opentype.js). Этот файл — КОРНЕВОЙ
      // провайдер, он лежит в главном бандле, поэтому импорт только динамический: иначе холст едет
      // в первый байт приложения ко всем, включая тех, кто редактор не открывает. Мерку зовут лишь
      // из редактора, чей чанк к этому моменту уже загружен, — второй загрузки не будет.
      const { QuoteFitService } = await import('@lite-workspace/shared/rust-canvas');
      return await measureQuoteLimits(this.injector.get(QuoteFitService), book, ownerPersonId, leadPersonId, leadReserve);
    } catch {
      return { limitDefault: undefined };
    }
  }
}

/**
 * Занятые эталоном кадры для панели фото ученика — тем же чтением структуры эталона, что и экран отбора
 * (`getMasterStructure`): ученику оно уже открыто. Одна структура на проект за сессию; отказ отдаёт панели
 * пустое множество (без вычета) и открывает повтор следующим `ensure`.
 *
 * Проверку распознавания (`faceDetection`) просит только редактор эталона; раз попросив, порт не теряет её
 * и в перечитываниях — иначе ответ без бита стёр бы подсказку. Перечитывание того же проекта держит прежний
 * ответ до нового: панель не мигает шторкой.
 */
@Injectable({ providedIn: 'root' })
export class FixedPhotosHostPort implements AlbumEditorFixedPhotosPort {
  private readonly assemblyApi = inject(BookAssemblyApiService);
  private readonly ids = signal<ReadonlySet<string> | undefined>(undefined);
  private readonly meta = signal<ReadonlyMap<string, FramePhotoMeta> | undefined>(undefined);
  private requestedProjectId: string | null = null;
  // Проект, чьи кадры и мета сейчас в сигналах (или грузятся): смена проекта сбрасывает их в «неизвестно».
  private shownProjectId: string | null = null;
  // Проект, для которого редактор эталона попросил проверку распознавания: просьба живёт до смены проекта.
  private faceDetectionProjectId: string | null = null;
  // Номер последнего чтения: ответ прежнего (другой проект или перечитывание того же) не перекрывает новый.
  private readSeq = 0;

  readonly fixedPhotoIds: Signal<ReadonlySet<string> | undefined> = this.ids.asReadonly();
  readonly framePhotoMeta: Signal<ReadonlyMap<string, FramePhotoMeta> | undefined> = this.meta.asReadonly();

  ensure(projectId: string, options?: AlbumEditorMasterStructureReadOptions): void {
    const askedFaceDetection = options?.faceDetection === true && this.faceDetectionProjectId !== projectId;
    if (askedFaceDetection) this.faceDetectionProjectId = projectId;
    if (this.requestedProjectId === projectId && !options?.refresh && !askedFaceDetection) return;
    if (this.shownProjectId !== projectId) {
      this.shownProjectId = projectId;
      this.ids.set(undefined);
      this.meta.set(undefined);
    }

    this.requestedProjectId = projectId;
    const read = ++this.readSeq;
    const request = this.faceDetectionProjectId === projectId ? { faceDetection: true } : undefined;
    firstValueFrom(this.assemblyApi.getMasterStructure(projectId, request))
      .then(structure => {
        if (read !== this.readSeq) return;
        this.ids.set(new Set<string>(structure.fixedPhotoIds ?? []));
        this.meta.set(new Map<string, FramePhotoMeta>(Object.entries(structure.framePhotoMeta ?? {})));
      })
      .catch(() => {
        if (read !== this.readSeq) return;
        // Панель без вычета лучше вечной шторки; подсказка замка без меты молчит. Прочитанное раньше
        // остаётся: неудачное перечитывание не отнимает у панели уже известные кадры.
        this.requestedProjectId = null;
        if (this.ids() === undefined) this.ids.set(new Set<string>());
      });
  }
}

/**
 * «Изделие» of a student album: the project's items named as the albums page names them, and the move
 * through the same S4 window. Moved → the albums page, where the album regenerates on its new item.
 */
@Injectable({ providedIn: 'root' })
export class StudentItemHostPort implements AlbumEditorStudentItemPort {
  private readonly projects = inject(ProjectsFacade);
  private readonly catalog = inject(PrintCatalogFacade);
  private readonly flow = inject(ItemMoveFlowService);
  private readonly sales = inject(SALES_DATA);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);
  // Products per item-id set: a settings change re-reads, the same set never twice; a failed product read drops that item.
  private readonly products = signal<ReadonlyMap<string, readonly PrintProductEntry[]>>(new Map());
  private readonly requested = new Set<string>();

  items(projectId: string): readonly AlbumEditorStudentItem[] {
    const products = this.productsOf(projectId);
    return products.map(product => ({ id: product.id, label: itemNameOf(products, product) }));
  }

  async move(projectId: string, personId: string, printProductId: string): Promise<void> {
    const product = this.productsOf(projectId).find(item => item.id === printProductId);
    if (!product) return;
    const person = (await this.projects.fullPersonRoster(projectId))?.find(item => item.id === personId);
    if (!person) {
      this.toast.error('Почему-то не удалось.. попробуйте снова');
      return;
    }

    // The same window as the albums page: the student's item and bill drive the surcharge, the lock and «уже оплачен»;
    // the editor holds neither the class bills nor, maybe, this project's album list.
    const [bills] = await Promise.all([this.sales.list(projectId), this.projects.loadAlbums(projectId)]).catch(() => [null]);
    if (!bills) {
      this.toast.error('Почему-то не удалось.. попробуйте снова');
      return;
    }

    const folder = isFolderKind(product.productKind);
    const main = this.projects.baseSettingsByProject()[projectId]?.photoBook?.printProductId;
    const from = studentMoveFrom(personId, this.projects.albums(), main, bills);
    if (!(await this.flow.move(projectId, person, { printProductId, folder }, undefined, from))) return;
    await this.router.navigate(['/projects', projectId, 'albums']);
    // ponytail: copy exists for the folder target only (plan T5.4); album target leaves without a toast.
    if (folder) {
      const name = firstNameGenitive(`${person.firstName} ${person.surname}`.trim());
      this.toast.success(name ? `Альбом ${name} стал папкой` : 'Альбом ученика стал папкой');
    }
  }

  private productsOf(projectId: string): readonly PrintProductEntry[] {
    const key = projectPrintItems(this.projects.baseSettingsByProject()[projectId]).join(',');
    const products = this.products().get(key);
    if (!products) untracked(() => this.request(key));
    return products ?? [];
  }

  private request(key: string): void {
    if (!key || this.requested.has(key)) return;
    this.requested.add(key);
    forkJoin(key.split(',').map(id => this.catalog.getProduct(id).pipe(catchError(() => of(null))))).subscribe(list =>
      this.products.update(map =>
        new Map(map).set(
          key,
          list.filter((product): product is PrintProductEntry => !!product),
        ),
      ),
    );
  }
}

/** The editor viewer's act + pill: «Фото»'s add/remove with its toasts, the cabinet's revert, «Исходник» swapped by the shell. */
@Injectable({ providedIn: 'root' })
export class AlbumRetouchLightbox implements RetouchLightboxPort {
  private readonly f = inject(ProjectsFacade);
  private readonly activity = inject(RetouchActivityService);
  private readonly toast = inject(ToastService);
  private readonly api = inject(RetouchApi);
  private readonly queue = inject(FotoclassRetouchQueue);
  private readonly scope = retouchScopeById(this.f);
  private readonly shown = signal<string | null>(null);
  private readonly shownSrc = retouchOriginalSrc(() => {
    const id = this.shown();
    return id ? this.originalUrl(id) : null;
  });

  readonly mode = this.queue.mode;

  photo(fileId: string): RetouchLightboxPhoto {
    const photo = this.scope().get(fileId);
    return {
      status: scopeMark(this.scope, this.activity, fileId),
      waiting: !!photo && !this.activity.isDone(photo),
      hasBackup: photo?.hasBackup === true,
    };
  }

  apply(fileId: string): void {
    this.queue.apply(fileId);
  }

  add(fileId: string): void {
    void this.queue.queue([fileId]);
  }

  remove(fileId: string): void {
    void this.queue.unqueue([fileId]);
  }

  revert(fileId: string): void {
    const projectId = this.f.currentProjectId();
    if (!projectId) return;
    void revertAndSettle(this.api, this.activity, projectId, [fileId])
      .then(() => {
        if (this.shown() === fileId) this.shown.set(null);
        this.activity.refresh(projectId);
      })
      .catch(() => this.toast.error(RETOUCH_COPY.revert.failed));
  }

  originalUrl(fileId: string): string {
    return retouchOriginalUrl(this.f.currentProjectId() ?? '', fileId);
  }

  showOriginal(fileId: string, on: boolean): void {
    this.shown.set(on ? fileId : null);
  }

  viewerSrc(fileId: string): string | null {
    return this.shown() === fileId ? this.shownSrc() : null;
  }
}

/** Root-visible ports: the root-provided editor store and data resolve these from the root injector. */
export function provideAlbumEditorHostRoot(): Provider[] {
  return [
    { provide: ALBUM_EDITOR_AUTH, useValue: { accessToken: () => readAccessToken() } },
    { provide: ALBUM_EDITOR_OUTGOING_BODY, useValue: stripClientPreviewMarker },
    { provide: ALBUM_EDITOR_BELOW_MIN_CUE, useValue: true },
    { provide: ALBUM_EDITOR_UPLOAD_QUEUE, useExisting: EditorUploadQueuePort },
    { provide: ALBUM_EDITOR_ALBUM_EVENTS, useExisting: EditorAlbumEventsPort },
    { provide: ALBUM_EDITOR_UPLOAD_PROGRESS, useValue: EditorUploadProgressStripComponent },
    // Роли живут в корне, а не на маршруте редактора: их читает и root-provided стор (право фотографа
    // на переключение типа разворота), а из корневого инжектора маршрутный провайдер не виден.
    // `ProjectRoleService` сам берёт проект из URL роутера, поэтому корню он ничего не должен.
    {
      provide: ALBUM_EDITOR_ROLES,
      useFactory: (): AlbumEditorRolesPort => {
        const roles = inject(ProjectRoleService);
        // Id зрителя перечитывается вместе с ролями проекта: та же сессия, что подписала запрос ролей.
        return {
          isOrganizerOnly: roles.isOrganizerOnly,
          roles: roles.roles,
          userId: computed(() => (roles.roles(), readAuthUserId())),
          superAdmin: roles.superAdmin,
        };
      },
    },
    {
      provide: EDITOR_VIEWER,
      useFactory: (): EditorViewer => {
        const roles = inject(ProjectRoleService);
        return { userId: computed(() => (roles.roles(), readAuthUserId())), superAdmin: roles.superAdmin };
      },
    },
    // Мастер-контекст и серверный пересчёт цитат читает root-provided стор при сохранении модели,
    // поэтому оба порта живут в корне: маршрутный провайдер из корневого инжектора не виден.
    { provide: ALBUM_EDITOR_STUDENT_HANDOFF, useExisting: StudentHandoffPort },
    { provide: ALBUM_EDITOR_STUDENT_ITEM, useExisting: StudentItemHostPort },
    { provide: ALBUM_EDITOR_DESIGN_OP, useExisting: EditorDesignOpPort },
    // Занятые эталоном кадры вычитает панель ученика в том же корневом сторе.
    { provide: ALBUM_EDITOR_FIXED_PHOTOS, useExisting: FixedPhotosHostPort },
    // Досчёт волн имён запускает резолвер роли; персон читает корневой стор — ждёт его тут, а не на маршруте.
    {
      provide: ALBUM_EDITOR_PERSONS_READ_GATE,
      useFactory: (): ((projectId: string) => Promise<void>) => {
        const nameWaves = inject(NameWaveSettleService);
        return projectId => nameWaves.settling(projectId);
      },
    },
    // Демо-вехи редактора (`demo_editor_opened`/`demo_editor_edited`) спрашивает тот же корневой стор:
    // один маршрутный провайдер ему не виден, и вехи молча не уходили вовсе.
    { provide: ALBUM_EDITOR_DOWNLOAD_GUARD, useExisting: DemoDownloadGuardService },
    { provide: ALBUM_EDITOR_COMMENTS, useClass: HttpAlbumEditorCommentsData },
    {
      provide: ALBUM_EDITOR_RETOUCH,
      useFactory: (): AlbumEditorRetouchPort => {
        const f = inject(ProjectsFacade);
        const roles = inject(ProjectRoleService);
        const activity = inject(RetouchActivityService);
        const lightbox = inject(AlbumRetouchLightbox);
        // The editor route sits outside the cabinet shell that loads the scope and follows the run events: both
        // start here once per project, photographer only (the scope route is owner-only).
        let loadedFor: string | null = null;
        effect(() => {
          const pid = f.currentProjectId();
          const owner = !roles.isOrganizerOnly() && !!roles.roles()?.some(role => OWNER_PREVIEW_ROLES.includes(role));
          if (!pid || pid === loadedFor || !owner || !f.retouchEnabled()) return;
          loadedFor = pid;
          untracked(() => {
            void f.loadRetouchScope(pid);
            void activity.watch(pid);
          });
        });
        const retouchedIds = computed<ReadonlySet<string>>(
          () =>
            new Set(
              f
                .retouchScope()
                .filter(photo => activity.isDone(photo))
                .map(photo => photo.id),
            ),
        );
        const scope = retouchScopeById(f);
        return {
          enabled: () => f.retouchEnabled(),
          available: () => f.retouchAvailable(),
          retouchedIds,
          markNeedsRetouch: fileId => activity.markNeedsRetouch([fileId]),
          status: fileId => scopeMark(scope, activity, fileId),
          lightboxPieces: { act: RetouchLightboxActComponent, pill: RetouchLightboxPillComponent },
          viewerSrc: fileId => lightbox.viewerSrc(fileId),
        };
      },
    },
    { provide: RETOUCH_LIGHTBOX_PORT, useExisting: AlbumRetouchLightbox },
  ];
}

type ScopePhoto = ReturnType<ProjectsFacade['retouchScope']>[number];

function retouchScopeById(f: ProjectsFacade): Signal<ReadonlyMap<string, ScopePhoto>> {
  return computed(() => new Map(f.retouchScope().map(photo => [photo.id, photo])));
}

/** The cabinet tile mark (`RetouchActivityService.markOf`); a photo outside the scope carries no tags and never waits. */
function scopeMark(
  scope: Signal<ReadonlyMap<string, ScopePhoto>>,
  activity: RetouchActivityService,
  fileId: string,
): RetouchStatus | null {
  const photo = scope().get(fileId);
  return activity.markOf(photo ?? { id: fileId }, !!photo);
}
